/**
 * GA4 loader – inject gtag only after analytics consent (no pre-consent network).
 */
(() => {
  const measurementId = window.KL24_GA4_ID;
  if (!measurementId) return;

  let analyticsGranted = false;
  let pageViewSent = false;
  let gtagLoading = false;
  let gtagReady = false;
  const pendingGrant = { want: false };

  const consentYes = (value) => value === 'yes' || value === true;

  const ensureDataLayer = () => {
    window.dataLayer = window.dataLayer || [];
    if (typeof window.gtag !== 'function') {
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
    }
  };

  const sendPageView = () => {
    if (!analyticsGranted || !gtagReady || pageViewSent) return;
    pageViewSent = true;
    window.gtag('event', 'page_view', {
      send_to: measurementId,
      page_location: window.location.href,
      page_title: document.title,
    });
  };

  const onGtagReady = () => {
    gtagReady = true;
    window.gtag('consent', 'default', {
      analytics_storage: analyticsGranted ? 'granted' : 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
    });
    window.gtag('js', new Date());
    window.gtag('config', measurementId, {
      send_page_view: false,
      anonymize_ip: true,
    });
    if (analyticsGranted) {
      window.gtag('consent', 'update', { analytics_storage: 'granted' });
      sendPageView();
    }
  };

  const loadGtag = () => {
    if (gtagReady || gtagLoading) return;
    gtagLoading = true;
    ensureDataLayer();
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    script.onload = onGtagReady;
    script.onerror = () => {
      gtagLoading = false;
    };
    document.head.appendChild(script);
  };

  const grantAnalytics = () => {
    analyticsGranted = true;
    pendingGrant.want = true;
    loadGtag();
    if (gtagReady) {
      window.gtag('consent', 'update', { analytics_storage: 'granted' });
      sendPageView();
    }
  };

  const denyAnalytics = () => {
    analyticsGranted = false;
    pendingGrant.want = false;
    if (gtagReady) {
      window.gtag('consent', 'update', { analytics_storage: 'denied' });
    }
  };

  const mapHubEvent = (name, detail) => {
    const base = {
      send_to: measurementId,
      event_category: 'kl24_hub',
      event_label: name,
    };
    switch (name) {
      case 'hub_tile_click':
        return { ...base, tile: detail.tile || '' };
      case 'hub_hero_cta_click':
      case 'hub_closing_cta_click':
        return { ...base, cta_variant: detail.variant || '', link_url: detail.href || '' };
      case 'hub_recent_single_click':
        return { ...base, variant_id: detail.variant_id || '', link_url: detail.href || '' };
      case 'hub_gap_set_click':
        return { ...base, set_handle: detail.set_handle || '' };
      case 'singles_filter_apply':
        return {
          ...base,
          event_category: 'kl24_singles_shop',
          filter_key: detail.filter_key || '',
          filter_value: detail.filter_value || '',
        };
      default:
        return { ...base, ...detail };
    }
  };

  const trackHubEvent = (name, detail = {}) => {
    if (!analyticsGranted || !gtagReady) return;
    window.gtag('event', name, mapHubEvent(name, detail));
  };

  const applyVisitorConsent = (consent) => {
    if (!consent) return;
    if (consentYes(consent.analytics) || consentYes(consent.analyticsAllowed)) {
      grantAnalytics();
      return;
    }
    if (consent.analytics === 'no' || consent.analyticsAllowed === false) {
      denyAnalytics();
    }
  };

  const initPrivacy = () => {
    try {
      const stored = localStorage.getItem('kl24_cookie_consent');
      if (stored === 'accepted') {
        grantAnalytics();
        return;
      }
      if (stored === 'rejected') {
        denyAnalytics();
        return;
      }
    } catch {
      /* ignore */
    }

    const privacy = window.Shopify?.customerPrivacy;
    if (!privacy) return;

    try {
      applyVisitorConsent(privacy.currentVisitorConsent?.());
    } catch {
      /* ignore */
    }

    document.addEventListener('visitorConsentCollected', (event) => {
      applyVisitorConsent(event.detail);
    });
  };

  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) initPrivacy();
    });
  } else {
    initPrivacy();
  }

  document.addEventListener('kl24:hub-analytics', (event) => {
    const detail = event.detail || {};
    const { name, ...rest } = detail;
    if (name) trackHubEvent(name, rest);
  });

  window.Kl24Ga4 = { trackHubEvent, grantAnalytics, denyAnalytics };
})();
